import type { FC, FormEvent } from 'react';
import { useCallback, useEffect, useState } from 'react';

import Button from '@trend-dev/trendtech-ui/components/UI/Button';
import TextField from '@trend-dev/trendtech-ui/components/UI/TextField';
import { useUpdatedRef } from '@trend-dev/ui-libs-utils/hooks/useUpdatedRef';

import { useCurrentUserStore } from 'store/currentUser';
import { userAdapter } from 'store/users/helpers/userAdapter';

import { useYM } from 'YM/Context';
import { GoalType } from 'YM/declare';
import { useSendErrorMetrics } from 'YM/hooks/useErrorMetrics';

import { Paper, Subtitle, Title, Wrapper } from 'components/UI/Paper';

import { provider } from 'providers';
import { TOKEN_TYPE } from 'providers/constants/tokenNames';

import { useDeviceWidth } from 'hooks/useDeviceWidth';
import { useRedirectTo } from 'hooks/useRedirectTo';

import { cookies } from 'utils/cookies';

import Agreement from './Agreement';
import { buttonText, inputPlaceholder, subtitle, title } from './const';
import styles from './styles.module.scss';

const Auth: FC = () => {
    const [agreementAccepted, setAgreementAccepted] = useState(false);
    const [name, setName] = useState('');
    const { setCurrentUser } = useCurrentUserStore();
    const redirect = useRedirectTo();
    const { isDesktop } = useDeviceWidth();
    const { triggerYM } = useYM();
    const sendErrorMetrics = useSendErrorMetrics();
    const sendErrorMetricsRef = useUpdatedRef(sendErrorMetrics);

    useEffect(() => {
        triggerYM({ goal: GoalType.AUTH_PAGE_VIEW });
    }, []);

    // если у пользователя уже есть клиентский / менеджерский токен - редирект
    useEffect(() => {
        if (provider.tokenName && provider.tokenName !== TOKEN_TYPE.EXTERNAL) {
            redirect();
        }
    }, []);

    const handleCheckboxChange = useCallback(() => {
        setAgreementAccepted(prevState => !prevState);
        triggerYM({ goal: GoalType.AUTH_PAGE_AGREEMENT });
    }, []);

    const handleInputNameChange = useCallback((_: string, value: string) => {
        setName(value);
    }, []);

    const handleSubmit = useCallback(
        async (e: FormEvent<HTMLFormElement>) => {
            e.preventDefault();

            try {
                cookies.set(TOKEN_TYPE.EXTERNAL, cookies.get(TOKEN_TYPE.EXTERNAL) ?? window.crypto.randomUUID());
                provider.updateToken();
                await provider.addExternalUser(name);
                const response = await provider.addUser();

                if ('_id' in response) {
                    setCurrentUser(userAdapter(response));
                    triggerYM({ goal: GoalType.AUTH_PAGE_JOIN });
                    redirect();
                } else {
                    throw new Error(JSON.stringify(response));
                }
            } catch (error) {
                sendErrorMetricsRef.current({ data: error, name: 'Регистрация гостя' });
            }

            return false;
        },
        [name],
    );

    const button = (className?: string, fullWidth?: boolean) => {
        const canBeSubmitted = !!(agreementAccepted && name.trim().length);

        return (
            <Button className={className} disabled={!canBeSubmitted} type='submit' kind='main' size='xl' borderRadius='md' fullWidth={fullWidth}>
                {buttonText}
            </Button>
        );
    };

    return (
        <form onSubmit={handleSubmit}>
            <Wrapper>
                <Paper>
                    <Title>{title}</Title>
                    <Subtitle>{subtitle}</Subtitle>
                    <div className={styles.row}>
                        <TextField
                            onChange={handleInputNameChange}
                            name='name'
                            insideIndent={3}
                            border={false}
                            size='xl'
                            fullWidth
                            placeholder={inputPlaceholder}
                            autoFocus
                            className={styles.field}
                        />
                        {isDesktop && button()}
                    </div>
                    <Agreement onChange={handleCheckboxChange} />
                </Paper>
            </Wrapper>
            {!isDesktop && <div className={styles.mobileButtonWrapper}>{button(styles.mobileButton)}</div>}
        </form>
    );
};

export default Auth;
